import React, { useCallback, useState } from 'react';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { differenceWith, unionWith } from 'lodash';
import style from './index.module.css';
import { RefinementListProvided } from 'react-instantsearch-core';
import { connectRefinementList } from 'react-instantsearch-dom';
import Checkbox from '../../checkbox';

type RefinementItem = {
    label: string;
    value: string[];
    isRefined: boolean;
    isMemory?: boolean;
};

const itemComparator = (a: RefinementItem, b: RefinementItem) => a.label === b.label;
const itemSortComparator = (a: RefinementItem, b: RefinementItem) => {
    // Send checked items to the top
    if (a.isRefined && !b.isRefined) return -1;
    if (!a.isRefined && b.isRefined) return 1;
    // Send grayed items to the bottom
    if (a.isMemory && !b.isMemory) return 1;
    if (!a.isMemory && b.isMemory) return -1;
    // Otherwise alphabetize
    return a.label.localeCompare(b.label);
};

const RefinementList = ({ items, refine }: RefinementListProvided) => {
    const [refinementMemory, setRefinementMemory] = useState<RefinementItem[]>([]);
    const [combinedItems, setCombinedItems] = useState<RefinementItem[]>([]);

    const itemsAreNew = () => {
        // This is the only dependable way to check if a new tab is being
        // loaded in an asynchronous framework (both Algolia fetches and
        // React updates to state)
        const diff = differenceWith(items, refinementMemory, itemComparator);
        return !!diff.length;
    };

    const combineItemsWithMemory = () => {
        const newCombinedItems = unionWith(items as RefinementItem[], refinementMemory, itemComparator);
        newCombinedItems.sort(itemSortComparator);
        setCombinedItems(newCombinedItems);
    };

    // It's important that we know we've received different items from Algolia,
    // so a mere reference comparison isn't going to help us
    useDeepCompareEffect(() => {
        // Cache the original, unrefined state of the filter the first time we receive items
        // TODO: this will need to be revisited if we persist filter states with URL params
        if (items.length && itemsAreNew()) {
            // Deep clone items in a way that doesn't make TypeScript angry
            const itemsAsMemory: RefinementItem[] = JSON.parse(JSON.stringify(items));

            // Only cached items have isMemory, and when we perform the union
            // with items what's in items will get precedence
            itemsAsMemory.forEach((item) => (item.isMemory = true));

            setRefinementMemory(itemsAsMemory);
        }

        // Reset memory if no items were received (happens during Algolia fetch)
        else if (!items.length && refinementMemory.length) {
            setRefinementMemory([]);
            setCombinedItems([]);
        }

        // Let filters reflect current filtering state
        else if (items.length) {
            combineItemsWithMemory();
        }
    }, [items]);

    useDeepCompareEffect(() => {
        // Trigger first display of filters after cache has been created
        if (refinementMemory.length) {
            combineItemsWithMemory();
        }
    }, [refinementMemory]);

    const [listExpanded, setListExpanded] = useState(false);
    const handleShowMoreClick = () => {
        listExpanded ? setListExpanded(false) : setListExpanded(true);
    };

    return (
        <>
            <ul className={style.options}>
                {combinedItems.slice(0, listExpanded ? combinedItems.length : 5).map((item) => (
                    <li key={item.label}>
                        <Checkbox
                            label={item.label}
                            checked={item.isRefined}
                            onChange={() => {
                                refine(item.value);
                            }}
                            disabled={item.isMemory}
                        />
                    </li>
                ))}
                {combinedItems.length > 5 && (
                    <li className={style.showMore}>
                        <button onClick={() => handleShowMoreClick()}>{listExpanded ? 'See less' : 'See all'}</button>
                    </li>
                )}
            </ul>
        </>
    );
};

const CustomRefinementList = connectRefinementList(RefinementList);

type Props = {
    attribute: string;
    label: string;
};

const WrappedFilter = ({ attribute, label }: Props) => {
    // Some filter label changes are necessary
    // (useCallback prevents an infinite loop)
    const itemTransformation = useCallback(
        (items: any) => {
            if (attribute === 'type') {
                return items.map((item: any) => ({
                    ...item,
                    label: item.label === 'Lot' ? 'Bid at Auction' : item.label,
                }));
            }
            return items;
        },
        [attribute],
    );

    return (
        <div className={style.filterWrapper}>
            <div className={style.accordionHeader}>{label}</div>
            <CustomRefinementList attribute={attribute} limit={9999} transformItems={itemTransformation} />
        </div>
    );
};

export default WrappedFilter;
